import { cn } from '../../lib/cn';

/**
 * The §4.1 button recipe, exported so a router link can wear it.
 *
 * `Button` renders an `<a>` when given `href`, which is right for external and
 * download URLs and wrong for internal navigation: a bare anchor reloads the
 * whole SPA. Most of the call-to-action buttons in the mockups go to another
 * page on this site, so the apps need the classes without the element.
 *
 * Same precedent as `linkClasses` and `CONTROL_CLASSES`: the recipe is the
 * shared thing, and a copy of it inside an app is how a design system drifts.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'gold' | 'inverse' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  // Navy pill. 10.7:1 white on navy-600.
  primary: 'bg-navy-600 text-white hover:bg-navy-700 active:bg-navy-800',
  // Outline navy.
  secondary:
    'bg-transparent text-navy-600 ring-1 ring-inset ring-navy-600 hover:bg-navy-50 active:bg-navy-100',
  ghost: 'bg-transparent text-navy-600 hover:bg-navy-50 active:bg-navy-100',
  // §1.5 — gold is permitted as a surface with dark text on it; what it may
  // never be is small text on white. Ink on gold-500 is 7.5:1.
  gold: 'bg-gold-500 text-ink hover:bg-gold-600 hover:text-white active:bg-gold-700',
  // White on navy: for use inside the dark bands (§1.1).
  inverse: 'bg-white text-navy-600 hover:bg-navy-50 active:bg-navy-100',
  /*
   * §4.1 lists five variants and this is a sixth.
   *
   * The design system was written for the public site, where nothing a
   * visitor can press destroys anything — the admin dashboard is full of
   * buttons that do, and giving `Delete` the same navy pill as `Save` is how
   * somebody deletes forty documents by muscle memory.
   *
   * `bg-danger` resolves to `--text-danger`, not `--danger`: the latter is
   * 4.2:1 behind white and would fail AA for the 14px semibold label these
   * buttons carry. The darkened one clears it at 4.9:1. See the note in the
   * preset, and tokens.css for why the two values exist.
   */
  danger: 'bg-danger text-white hover:opacity-90 active:opacity-80',
};

/**
 * Heights are 36 / 44 / 48px. The mockups sit at the middle size; 44px is also
 * the WCAG 2.5.5 target-size guideline, which matters on the mobile drawer.
 *
 * A floor rather than a fixed height, because labels here are Georgian.
 * `მარეგულირებელი დოკუმენტები` is 25 characters and wraps to two lines in the
 * footer's first column — and a wrapped label inside `h-9` does not make the
 * button taller, it spills out through the top and bottom of the pill, with the
 * outline cutting across the text. `min-h-*` keeps the target size and lets the
 * button grow instead.
 *
 * Single-line buttons are unchanged: the padding is small enough that the line
 * box plus padding is still under the floor at every size, so the rendered
 * height is the same 36 / 44 / 48 it was. Only a label that already wrapped
 * moves, and it moves from broken to correct.
 *
 * This is the shape of bug the design system will keep producing — the recipe
 * was written against Latin mockup copy, and Georgian compounds are routinely
 * half again as long (CLAUDE.md rule 1).
 */
export const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'min-h-9 gap-1.5 px-4 py-1 text-body-sm',
  md: 'min-h-11 gap-2 px-6 py-1.5 text-body-sm',
  lg: 'min-h-12 gap-2 px-8 py-2 text-body',
};

export const BUTTON_BASE =
  // `text-center` so the second line of a wrapped label is centred under the
  // first rather than ragged against the start edge.
  'inline-flex items-center justify-center text-center rounded-full font-semibold no-underline ' +
  'transition-colors duration-fast ease-out ' +
  'disabled:pointer-events-none disabled:opacity-50 ' +
  'aria-disabled:pointer-events-none aria-disabled:opacity-50';

export function buttonClasses(
  options: {
    variant?: ButtonVariant;
    size?: ButtonSize;
    fullWidth?: boolean;
  } = {},
  className?: string,
): string {
  const { variant = 'primary', size = 'md', fullWidth = false } = options;

  return cn(
    BUTTON_BASE,
    BUTTON_VARIANTS[variant],
    BUTTON_SIZES[size],
    fullWidth && 'w-full',
    className,
  );
}
